import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import Plyr from 'plyr';
import 'plyr/dist/plyr.css';
import {
  extractYouTubeVideoId,
  getMagYouTubePlayerVars,
} from '../../utils/youtubeEmbed';
import { fetchYouTubeNoembed } from '../../services/noembedService';

export type PlyrYouTubePlayerHandle = {
  seekTo: (amount: number, type?: 'seconds' | 'fraction') => void;
  getCurrentTime: () => number;
  getDuration: () => number;
  play: () => void;
  pause: () => void;
};

type Props = {
  url: string;
  title?: string;
  className?: string;
  onProgress?: (state: {
    played: number;
    playedSeconds: number;
    loaded: number;
    loadedSeconds: number;
  }) => void;
  onDuration?: (duration: number) => void;
  onPlay?: () => void;
  onPause?: () => void;
};

/** Safely read a Plyr getter — media can be null during destroy / remount. */
function safeGet(player: Plyr | null, key: 'currentTime' | 'duration' | 'buffered'): number {
  if (!player) return 0;
  try {
    const value = player[key];
    return typeof value === 'number' && Number.isFinite(value) ? value : 0;
  } catch {
    return 0;
  }
}

/**
 * Mag-style Plyr YouTube player.
 * Uses data-plyr-provider (Plyr builds the YT.Player itself with controls=0).
 */
const PlyrYouTubePlayer = forwardRef<PlyrYouTubePlayerHandle, Props>(
  (
    {
      url,
      title = 'YouTube video player',
      className = '',
      onProgress,
      onDuration,
      onPlay,
      onPause,
    },
    ref
  ) => {
    const targetRef = useRef<HTMLDivElement>(null);
    const playerRef = useRef<Plyr | null>(null);
    const destroyedRef = useRef(false);
    const progressTimerRef = useRef<number | null>(null);
    const onProgressRef = useRef(onProgress);
    const onDurationRef = useRef(onDuration);
    const onPlayRef = useRef(onPlay);
    const onPauseRef = useRef(onPause);
    const [poster, setPoster] = useState<string | undefined>(undefined);

    onProgressRef.current = onProgress;
    onDurationRef.current = onDuration;
    onPlayRef.current = onPlay;
    onPauseRef.current = onPause;

    const videoId = extractYouTubeVideoId(url);

    useImperativeHandle(ref, () => ({
      seekTo: (amount: number, type: 'seconds' | 'fraction' = 'fraction') => {
        const player = playerRef.current;
        if (!player || destroyedRef.current) return;
        try {
          const duration = safeGet(player, 'duration');
          player.currentTime =
            type === 'fraction' ? amount * duration : amount;
        } catch {
          // ignore — media may be gone
        }
      },
      getCurrentTime: () => safeGet(playerRef.current, 'currentTime'),
      getDuration: () => safeGet(playerRef.current, 'duration'),
      play: () => {
        if (destroyedRef.current) return;
        try {
          void playerRef.current?.play();
        } catch {
          // ignore
        }
      },
      pause: () => {
        if (destroyedRef.current) return;
        try {
          playerRef.current?.pause();
        } catch {
          // ignore
        }
      },
    }));

    useEffect(() => {
      if (!videoId) return;
      setPoster(`https://i.ytimg.com/vi/${videoId}/maxresdefault.jpg`);
      let cancelled = false;
      fetchYouTubeNoembed(url).then((meta) => {
        if (cancelled || !meta?.thumbnail_url) return;
        setPoster(meta.thumbnail_url);
      });
      return () => {
        cancelled = true;
      };
    }, [url, videoId]);

    useEffect(() => {
      if (!videoId || !targetRef.current) return;

      destroyedRef.current = false;

      const clearProgress = () => {
        if (progressTimerRef.current != null) {
          window.clearInterval(progressTimerRef.current);
          progressTimerRef.current = null;
        }
      };

      const startProgress = () => {
        clearProgress();
        progressTimerRef.current = window.setInterval(() => {
          if (destroyedRef.current) {
            clearProgress();
            return;
          }
          const p = playerRef.current;
          if (!p || !onProgressRef.current) return;
          const duration = safeGet(p, 'duration');
          const current = safeGet(p, 'currentTime');
          const loaded = safeGet(p, 'buffered');
          onProgressRef.current({
            played: duration > 0 ? current / duration : 0,
            playedSeconds: current,
            loaded,
            loadedSeconds: duration * loaded,
          });
        }, 250);
      };

      let player: Plyr;
      try {
        player = new Plyr(targetRef.current, {
          title,
          seekTime: 15,
          controls: [
            'play-large',
            'progress',
            'rewind',
            'play',
            'fast-forward',
            'current-time',
            'mute',
            'volume',
            'settings',
            'fullscreen',
          ],
          settings: ['speed'],
          speed: { selected: 1, options: [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2] },
          i18n: {
            rewind: 'رجوع {seektime} ثانية',
            fastForward: 'تقديم {seektime} ثانية',
          },
          // Mag-style playerVars → same embed query string as Mag Academy
          youtube: {
            ...getMagYouTubePlayerVars(),
          },
          hideControls: true,
          clickToPlay: true,
          keyboard: { focused: true, global: false },
          tooltips: { controls: true, seek: true },
          fullscreen: { enabled: true, fallback: true, iosNative: false },
          storage: { enabled: false },
          ratio: '16:9',
        });
      } catch {
        return;
      }

      playerRef.current = player;

      /**
       * Ensure Mag/Plyr crop wrapper exists (YT.Player may inject iframe without it).
       * Crop % lives in CSS (portrait vs landscape/fullscreen) — avoid inline values
       * that would override the landscape fix and clip the video top.
       */
      const ensureChromeCrop = () => {
        if (destroyedRef.current) return;
        const root = targetRef.current?.closest('.plyr') as HTMLElement | null;
        if (!root) return;

        const embed = root.querySelector(
          '.plyr__video-embed, .plyr__video-wrapper'
        ) as HTMLElement | null;
        if (!embed) return;

        embed.style.overflow = 'hidden';

        let crop = embed.querySelector(
          '.plyr__video-embed__container'
        ) as HTMLElement | null;

        const iframe = embed.querySelector('iframe');
        if (!crop && iframe && iframe.parentElement === embed) {
          crop = document.createElement('div');
          crop.className = 'plyr__video-embed__container';
          embed.insertBefore(crop, iframe);
          crop.appendChild(iframe);
        }

        if (crop) {
          crop.style.position = 'relative';
          crop.style.height = 'auto';
          crop.style.width = '100%';
          // Clear any stale inline crop so CSS orientation/fullscreen rules apply
          crop.style.removeProperty('padding-bottom');
          crop.style.removeProperty('transform');
        }

        if (iframe) {
          iframe.style.pointerEvents = 'none';
          iframe.setAttribute('tabindex', '-1');
          iframe.title = title;
        }
      };

      const onReady = () => {
        if (destroyedRef.current) return;
        onDurationRef.current?.(safeGet(player, 'duration'));
        if (poster) {
          try {
            player.poster = poster;
          } catch {
            // ignore
          }
        }
        ensureChromeCrop();
        window.setTimeout(ensureChromeCrop, 100);
        window.setTimeout(ensureChromeCrop, 400);
        startProgress();
      };

      const onMeta = () => {
        if (destroyedRef.current) return;
        onDurationRef.current?.(safeGet(player, 'duration'));
        ensureChromeCrop();
      };

      const onPlayEvt = () => {
        if (destroyedRef.current) return;
        ensureChromeCrop();
        onPlayRef.current?.();
      };
      const onPauseEvt = () => {
        if (!destroyedRef.current) onPauseRef.current?.();
      };

      player.on('ready', onReady);
      player.on('loadedmetadata', onMeta);
      player.on('play', onPlayEvt);
      player.on('pause', onPauseEvt);
      player.on('ended', onPauseEvt);
      player.on('enterfullscreen', ensureChromeCrop);
      player.on('exitfullscreen', ensureChromeCrop);

      const onOrientationOrResize = () => {
        ensureChromeCrop();
      };
      window.addEventListener('orientationchange', onOrientationOrResize);
      window.addEventListener('resize', onOrientationOrResize);

      return () => {
        destroyedRef.current = true;
        clearProgress();
        playerRef.current = null;
        window.removeEventListener('orientationchange', onOrientationOrResize);
        window.removeEventListener('resize', onOrientationOrResize);
        try {
          player.destroy();
        } catch {
          // ignore
        }
      };
    }, [videoId, title]);

    useEffect(() => {
      if (destroyedRef.current || !playerRef.current || !poster) return;
      try {
        playerRef.current.poster = poster;
      } catch {
        // ignore
      }
    }, [poster]);

    if (!videoId) {
      return (
        <div
          className={`flex h-full w-full items-center justify-center bg-slate-900 text-sm text-white ${className}`}
        >
          رابط الفيديو غير صالح
        </div>
      );
    }

    return (
      <div className={`plyr-youtube-host ${className}`}>
        {/* Plyr builds YT.Player from data attributes (no pre-built iframe) */}
        <div
          key={videoId}
          ref={targetRef}
          data-plyr-provider="youtube"
          data-plyr-embed-id={videoId}
        />
      </div>
    );
  }
);

PlyrYouTubePlayer.displayName = 'PlyrYouTubePlayer';

export default PlyrYouTubePlayer;
